import React, { useState, useEffect, useRef } from 'react';
import articleService from '../../../services/articleService';
import clientService from '../../../services/clientService';
import typeArticleService from '../../../services/typeArticleService';
import localService from '../../../services/localService';
import { useTheme } from '../../../contexts/ThemeContext';
import ClientSelectDropdown from '../../common/ClientSelectDropdown';
import LocalSelectDropdown from '../../common/LocalSelectDropdown';
import TypeArticleSelectDropdown from '../../common/TypeArticleSelectDropdown';
import { openBarcodeScanner } from '../../../utils/barcodeScanner';

const AddArticleModal = ({ isOpen, onClose, onSuccess }) => {
  const [formData, setFormData] = useState({
    designation: '',
    typeArticleId: '',
    capacite: '',
    marque: '',
    codeABarre: '',
    numeroSerie: '',
    emplacement: '',
    observation: '',
    localId: '',
    clientId: '',
    pressionPermanente: true,
    datetimeFabrication: '',
    enService: true
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  
  // Camera scanning states
  const [showCamera, setShowCamera] = useState(false);
  const [barcodeScanning, setBarcodeScanning] = useState(false);
  const scannerRef = useRef(null);

  // Dropdown data and loading states
  const [typeArticles, setTypeArticles] = useState([]);
  const [clients, setClients] = useState([]);
  const [locaux, setLocaux] = useState([]);
  const [loadingData, setLoadingData] = useState({
    typeArticles: false,
    clients: false,
    locaux: false
  });

  // Search states for dropdowns
  const [searchStates, setSearchStates] = useState({
    typeArticle: '',
    client: '',
    local: ''
  });

  // Fetch data when modal opens
  useEffect(() => {
    if (isOpen) {
      fetchTypeArticles();
      fetchClients();
    }
  }, [isOpen]);

  // Fetch locaux when client changes
  useEffect(() => {
    if (isOpen) {
      fetchLocaux();
    }
  }, [isOpen, formData.clientId]);

  const fetchTypeArticles = async () => {
    setLoadingData(prev => ({ ...prev, typeArticles: true }));
    try {
      const result = await typeArticleService.getTypeArticles(50, 1);
      console.log('Type Articles API Response:', result);
      
      if (result.success && result.data?.list) {
        setTypeArticles(result.data.list);
      } else if (result.success && result.data?.data) {
        setTypeArticles(result.data.data);
      } else {
        console.warn('Unexpected type articles response structure:', result);
        setTypeArticles([]);
      }
    } catch (error) {
      console.error('Error fetching type articles:', error);
      setTypeArticles([]);
    } finally {
      setLoadingData(prev => ({ ...prev, typeArticles: false }));
    }
  };

  const fetchClients = async () => {
    setLoadingData(prev => ({ ...prev, clients: true }));
    try {
      const result = await clientService.getClients(100, 1);
      console.log('Clients API Response:', result);
      
      if (result.success && result.data?.list) {
        setClients(result.data.list);
      } else if (result.success && result.data?.data) {
        setClients(result.data.data);
      } else {
        console.warn('Unexpected clients response structure:', result);
        setClients([]);
      }
    } catch (error) {
      console.error('Error fetching clients:', error);
      setClients([]);
    } finally {
      setLoadingData(prev => ({ ...prev, clients: false }));
    }
  };

  const fetchLocaux = async () => {
    setLoadingData(prev => ({ ...prev, locaux: true }));
    try {
      // Filter locaux by selected client if client is selected
      const filterObj = {};
      if (formData.clientId) {
        filterObj.clientId = formData.clientId;
      }
      
      const result = await localService.getLocaux(100, 1, filterObj);
      console.log('Locaux API Response:', result);
      
      if (result.success && result.data?.list) {
        setLocaux(result.data.list);
      } else if (result.success && result.data?.data) {
        setLocaux(result.data.data);
      } else {
        console.warn('Unexpected locaux response structure:', result);
        setLocaux([]);
      }
    } catch (error) {
      console.error('Error fetching locaux:', error);
      setLocaux([]);
    } finally {
      setLoadingData(prev => ({ ...prev, locaux: false }));
    }
  };

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value
    }));
  };

  // Handle client change - reset local selection when client changes
  const handleClientChange = (clientId) => {
    setFormData(prev => ({
      ...prev,
      clientId: clientId,
      localId: '' // Reset local selection when client changes
    }));
  };

  const handleSearchChange = (field, value) => {
    setSearchStates(prev => ({
      ...prev,
      [field]: value
    }));
  };

  // Filter functions for dropdowns
  const getFilteredTypeArticles = () => {
    if (!searchStates.typeArticle) return typeArticles;
    return typeArticles.filter(type => 
      type.designation.toLowerCase().includes(searchStates.typeArticle.toLowerCase())
    );
  };

  const getFilteredClients = () => {
    if (!searchStates.client) return clients;
    return clients.filter(client => 
      (client.raisonSocial || '').toLowerCase().includes(searchStates.client.toLowerCase()) ||
      `${client.nom || ''} ${client.prenom || ''}`.toLowerCase().includes(searchStates.client.toLowerCase()) ||
      (client.matriculeFiscal || '').toLowerCase().includes(searchStates.client.toLowerCase())
    );
  };

  const getFilteredLocaux = () => {
    if (!searchStates.local) return locaux;
    return locaux.filter(local => 
      (local.adresse || '').toLowerCase().includes(searchStates.local.toLowerCase()) ||
      (local.responsable || '').toLowerCase().includes(searchStates.local.toLowerCase())
    );
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    setError('');

    try {
      // Prepare the payload according to API specification
      let payload = {
        id: 0, // For new articles
        designation: formData.designation.trim(),
        localId: formData.localId ? parseInt(formData.localId, 10) : null,
        clientId: formData.clientId ? parseInt(formData.clientId, 10) : null,
        emplacement: formData.emplacement.trim(),
        codeABarre: formData.codeABarre.trim(),
        observation: formData.observation.trim() || null,
        typeArticleId: parseInt(formData.typeArticleId, 10),
        capacite: parseFloat(formData.capacite),
        pressionPermanente: formData.pressionPermanente,
        marque: formData.marque.trim(),
        datetimeFabrication: formData.datetimeFabrication ? new Date(formData.datetimeFabrication).toISOString() : null,
        numeroSerie: formData.numeroSerie.trim() || null,
        enService: formData.enService, // Add this field
        // These nested objects should be null for creation - the API will handle the relationships
        local: null,
        client: null,
        typeArticle: null
      };

      console.log('Sending payload:', payload);

      const result = await articleService.createArticle(payload);
      
      if (result.success) {
        onSuccess();
        onClose();
        resetForm();
      } else {
        setError(result.message || 'Erreur lors de la création de l\'article');
      }
    } catch (error) {
      console.error('Create article error:', error);
      setError('Erreur lors de la création de l\'article');
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      designation: '',
      typeArticleId: '',
      capacite: '',
      marque: '',
      codeABarre: '',
      numeroSerie: '',
      emplacement: '',
      observation: '',
      localId: '',
      clientId: '',
      pressionPermanente: true,
      datetimeFabrication: '',
      enService: true // Add this field
    });
    setSearchStates({
      typeArticle: '',
      client: '',
      local: ''
    });
    setError('');
  };

  const handleClose = () => {
    resetForm();
    onClose();
  };

  const { isDarkMode } = useTheme();

  // Stop scanning function
  const stopScanning = () => {
    if (scannerRef.current) {
      scannerRef.current.cleanup();
      scannerRef.current = null;
    }
    setShowCamera(false);
    setBarcodeScanning(false);
  };

  // Start barcode scanning using Quagga2
  const startBarcodeScanning = () => {
    setBarcodeScanning(true);
    setShowCamera(true);

    const scanner = openBarcodeScanner({
      modalId: 'barcode-scanner-modal-add',
      scannerId: 'quagga-scanner-add',
      onDetected: (barcode) => {
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        handleScanResult(barcode);
      },
      onError: () => {
        setBarcodeScanning(false);
        setShowCamera(false);
        const userInput = prompt('Caméra non disponible. Saisissez le code-barres manuellement (8 chiffres maximum):');
        if (userInput && userInput.trim()) {
          const trimmedInput = userInput.trim();
          if (trimmedInput.length <= 8) {
            handleScanResult(trimmedInput);
          }
        }
      },
      onManualEntry: () => {
        scannerRef.current = null;
        setBarcodeScanning(false);
        setShowCamera(false);
        const userInput = prompt('Saisissez le code-barres manuellement (max 8 chiffres):');
        if (userInput && userInput.trim()) {
          const trimmedInput = userInput.trim();
          if (trimmedInput.length <= 8) {
            handleScanResult(trimmedInput);
          }
        }
      },
    });
    scannerRef.current = scanner;
  };


  // Handle QR/Barcode scan result with validation
  const handleScanResult = (result) => {
    if (result) {
      // Validate barcode length before processing
      if (result.length > 8) {
        setError('❌ Code invalide! Les codes-barres doivent faire 8 chiffres maximum.');
        return;
      }
      
      setFormData(prev => ({ ...prev, codeABarre: result }));
      setShowCamera(false);
      // Show success toast if you have toast implementation
      console.log(`Code scanné: ${result}`);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[9999] p-4">
      <div className={`rounded-2xl shadow-2xl w-full max-w-4xl max-h-[90vh] overflow-hidden flex flex-col ${isDarkMode ? 'bg-gray-800' : 'bg-white'}`}>
        {/* Header */}
        <div className={`px-6 py-4 flex-shrink-0 ${
          isDarkMode
            ? 'bg-gray-800' // Match the dark body background
            : 'bg-gradient-to-r from-primary-600 to-primary-700'
        }`}>
          <div className="flex items-center justify-between">
            <div className="flex items-center">
              <div className="w-10 h-10 bg-white/20 rounded-xl flex items-center justify-center mr-3">
                <i className="fas fa-plus text-white text-lg"></i>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">Nouvel Article</h2>
                <p className="text-primary-100 text-sm">Ajouter un nouvel équipement de sécurité</p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              className="text-white hover:bg-white/20 rounded-lg p-2 transition-colors"
            >
              <i className="fas fa-times text-xl"></i>
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="overflow-y-auto flex-1" style={{ maxHeight: 'calc(90vh - 140px)' }}>
          <div className="p-6">
            {error && (
              <div className={`mb-6 p-4 rounded-xl border-l-4 border-l-red-500 ${
                isDarkMode 
                  ? 'bg-red-900/50 text-red-300' 
                  : 'bg-red-50 text-red-700'
              }`}>
                <div className="flex items-center">
                  <i className="fas fa-exclamation-circle mr-3 text-red-500"></i>
                  <div>
                    <div className="font-medium">Erreur de validation</div>
                    <div className="text-sm">{error}</div>
                  </div>
                </div>
              </div>
            )}
            
            <form onSubmit={handleSubmit} className="space-y-6">
              {/* Basic Information */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-gray-50'}`}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-blue-100 rounded-lg mr-3">
                    <i className="fas fa-info-circle text-blue-600"></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations générales</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-tag mr-2 text-gray-400"></i>
                      Désignation *
                    </label>
                    <input 
                      type="text" 
                      name="designation"
                      value={formData.designation}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Ex: EXT001, EXT-POUDRE-001"
                    />
                  </div>
                  
                  {/* Type Article with Integrated Search */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-layer-group mr-2 text-gray-400"></i>
                      Type d'article *
                    </label>
                    <TypeArticleSelectDropdown
                      value={formData.typeArticleId}
                      onChange={val => setFormData(prev => ({ ...prev, typeArticleId: val }))}
                      options={typeArticles}
                      loading={loadingData.typeArticles}
                      disabled={loadingData.typeArticles}
                      isDarkMode={isDarkMode}
                      required={true}
                      placeholder="Sélectionner un type"
                    />
                  </div>
                </div>
              </div>

              {/* Technical Specifications */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-green-50'}`}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-green-100 rounded-lg mr-3">
                    <i className="fas fa-cogs text-green-600"></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Spécifications techniques</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-weight mr-2 text-gray-400"></i>
                      Capacité (kg) *
                    </label>
                    <input 
                      type="number" 
                      name="capacite"
                      value={formData.capacite}
                      onChange={handleChange}
                      required
                      min="0"
                      step="0.1"
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Ex: 6, 2.5, 1"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-industry mr-2 text-gray-400"></i>
                      Marque *
                    </label>
                    <input 
                      type="text" 
                      name="marque"
                      value={formData.marque}
                      onChange={handleChange}
                      required
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Ex: Tuffco, Gloria, Sicli"
                    />
                  </div>
                  
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-barcode mr-2 text-gray-400"></i>
                      Code à barres *
                    </label>
                    <div className="flex gap-2">
                      <input 
                        type="text" 
                        name="codeABarre"
                        value={formData.codeABarre}
                        onChange={(e) => {
                          const value = e.target.value;
                          // Validate length on input
                          if (value.length <= 8) {
                            handleChange(e);
                          } else {
                            setError('❌ Code invalide! Les codes-barres doivent faire 8 chiffres maximum.');
                          }
                        }}
                        required
                        maxLength={8}
                        className={`flex-1 px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                        placeholder="Ex: 10000588 (max 8 chiffres)"
                      />
                      <button
                        type="button"
                        onClick={() => {
                          if (barcodeScanning) {
                            stopScanning();
                          } else {
                            startBarcodeScanning();
                          }
                        }}
                        className={`px-2 sm:px-3 py-2 rounded-lg font-medium transition-colors flex items-center justify-center ${
                          barcodeScanning 
                            ? 'bg-red-600 text-white hover:bg-red-700' 
                            : isDarkMode 
                              ? 'bg-primary-700 text-white hover:bg-primary-800' 
                              : 'bg-primary-600 text-white hover:bg-primary-700'
                        }`}
                        title={barcodeScanning ? "Arrêter le scanner" : "Scanner avec la caméra"}
                        disabled={barcodeScanning}
                      >
                        <i className={`fas ${barcodeScanning ? 'fa-stop' : 'fa-camera'} ${barcodeScanning ? 'animate-pulse' : ''} text-sm sm:text-base`}></i>
                        {/* Show text on larger screens only */}
                        <span className="hidden sm:inline ml-1 text-sm">
                          {barcodeScanning ? 'Arrêter' : 'Scanner'}
                        </span>
                      </button>
                    </div>
                    
                    {/* Responsive Camera Scanner Status */}
                    {barcodeScanning && (
                      <div className={`mt-2 p-2 sm:p-3 rounded-lg border-2 border-dashed ${
                        isDarkMode 
                          ? 'border-primary-700 bg-gray-900 text-primary-300' 
                          : 'border-primary-500 bg-primary-50 text-primary-700'
                      } text-center text-xs sm:text-sm`}>
                        <div className="flex items-center justify-center gap-1 sm:gap-2">
                          <i className="fas fa-camera animate-pulse text-sm"></i>
                          <span className="text-xs sm:text-sm">
                            <span className="hidden sm:inline">Scanner activé - Pointez vers le code-barres</span>
                            <span className="sm:hidden">Scanner actif</span>
                          </span>
                        </div>
                        {/* Additional mobile tip */}
                        <div className="mt-1 text-xs opacity-75 sm:hidden">
                          Pointez vers le code-barres (max 8 chiffres)
                        </div>
                      </div>
                    )}
                    
                    {/* Length validation indicator */}
                    {formData.codeABarre && formData.codeABarre.length > 8 && (
                      <div className="mt-1 text-xs text-red-600 flex items-center">
                        <i className="fas fa-exclamation-triangle mr-1"></i>
                        <span>Maximum 8 chiffres autorisés</span>
                      </div>
                    )}
                  </div>

                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-hashtag mr-2 text-gray-400"></i>
                      Numéro de série
                    </label>
                    <input 
                      type="text" 
                      name="numeroSerie"
                      value={formData.numeroSerie}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                      placeholder="Ex: 0285, SN-2024-001"
                    />
                  </div>
                </div>
              </div>

              {/* Location & Client Info with Integrated Search */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-amber-50'}`}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-amber-100 rounded-lg mr-3">
                    <i className="fas fa-map-marker-alt text-amber-600"></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Localisation et client</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  {/* Client with Integrated Search */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-user mr-2 text-gray-400"></i>
                      Client
                    </label>
                    <div className="relative">
                      <ClientSelectDropdown
                        value={formData.clientId}
                        onChange={handleClientChange}
                        options={clients}
                        loading={loadingData.clients}
                        disabled={loadingData.clients}
                        isDarkMode={isDarkMode}
                        required={false}
                        placeholder="Sélectionner un client"
                      />
                    </div>
                  </div>
                  
                  {/* Local with Integrated Search - depends on selected client */}
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-building mr-2 text-gray-400"></i>
                      Local
                      {formData.clientId && (
                        <span className={`ml-2 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                          (filtré par client)
                        </span>
                      )}
                    </label>
                    <div className="relative">
                      <LocalSelectDropdown
                        value={formData.localId}
                        onChange={val => setFormData(prev => ({ ...prev, localId: val }))}
                        options={locaux}
                        loading={loadingData.locaux}
                        disabled={loadingData.locaux}
                        isDarkMode={isDarkMode}
                        required={false}
                        placeholder={
                          formData.clientId 
                            ? "Sélectionner un local pour ce client"
                            : "Sélectionner d'abord un client"
                        }
                      />
                    </div>
                    {formData.clientId && locaux.length === 0 && !loadingData.locaux && (
                      <p className={`mt-1 text-xs ${isDarkMode ? 'text-gray-400' : 'text-gray-500'}`}>
                        <i className="fas fa-info-circle mr-1"></i>
                        Aucun local trouvé pour ce client
                      </p>
                    )}
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <i className="fas fa-location-arrow mr-2 text-gray-400"></i>
                    Emplacement *
                  </label>
                  <input 
                    type="text" 
                    name="emplacement"
                    value={formData.emplacement}
                    onChange={handleChange}
                    required
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                    placeholder="Ex: chambre gardien, hall d'entrée, bureau 201"
                  />
                </div>
              </div>

              {/* Additional Information */}
              <div className={`rounded-xl p-6 ${isDarkMode ? 'bg-gray-700' : 'bg-purple-50'}`}>
                <div className="flex items-center mb-4">
                  <div className="p-2 bg-purple-100 rounded-lg mr-3">
                    <i className="fas fa-plus-circle text-purple-600"></i>
                  </div>
                  <h3 className={`text-lg font-semibold ${isDarkMode ? 'text-white' : 'text-gray-900'}`}>Informations complémentaires</h3>
                </div>
                
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
                  <div>
                    <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                      <i className="fas fa-calendar mr-2 text-gray-400"></i>
                      Date de fabrication
                    </label>
                    <input 
                      type="date" 
                      name="datetimeFabrication"
                      value={formData.datetimeFabrication}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                    />
                  </div>
                  
                  <div className="space-y-4">
                    <div className="flex items-center">
                      <div className={`flex items-center p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                        <input 
                          type="checkbox" 
                          name="pressionPermanente"
                          checked={formData.pressionPermanente}
                          onChange={handleChange}
                          className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                        />
                        <label className={`text-sm font-medium flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <i className="fas fa-tachometer-alt mr-2 text-gray-400"></i>
                          Pression permanente
                        </label>
                      </div>
                    </div>
                    
                    <div className="flex items-center">
                      <div className={`flex items-center p-4 rounded-lg border ${isDarkMode ? 'bg-gray-800 border-gray-600' : 'bg-white border-gray-300'}`}>
                        <input 
                          type="checkbox" 
                          name="enService"
                          checked={formData.enService}
                          onChange={handleChange}
                          className="mr-3 rounded border-gray-300 text-primary-600 focus:ring-primary-500 h-4 w-4"
                        />
                        <label className={`text-sm font-medium flex items-center ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                          <i className="fas fa-power-off mr-2 text-gray-400"></i>
                          En service
                        </label>
                      </div>
                    </div>
                  </div>
                </div>
                
                <div>
                  <label className={`block text-sm font-medium mb-2 ${isDarkMode ? 'text-gray-300' : 'text-gray-700'}`}>
                    <i className="fas fa-sticky-note mr-2 text-gray-400"></i>
                    Observations
                  </label>
                  <textarea 
                    rows="4"
                    name="observation"
                    value={formData.observation}
                    onChange={handleChange}
                    className={`w-full px-4 py-3 border rounded-lg focus:ring-2 focus:border-transparent transition-colors resize-none ${isDarkMode ? 'bg-gray-800 border-gray-600 text-white placeholder-gray-400 focus:ring-dark-primary-500' : 'bg-white border-gray-300 text-gray-900 placeholder-gray-500 focus:ring-primary-500'}`}
                    placeholder="Observations, notes, remarques particulières sur cet article..."
                  ></textarea>
                </div>
              </div>
            </form>
          </div>
        </div>

        {/* Footer - Fixed at bottom */}
        <div className={`border-t px-6 py-4 flex-shrink-0 ${isDarkMode ? 'border-gray-700 bg-gray-800' : 'border-gray-200 bg-gray-50'}`}>
          <div className="flex flex-col sm:flex-row justify-end gap-3">
            <button 
              type="button"
              onClick={handleClose}
              className={`px-6 py-3 border rounded-lg transition-colors font-medium ${
                isDarkMode 
                  ? 'text-gray-300 bg-gray-700 border-gray-600 hover:bg-gray-600' 
                  : 'text-gray-600 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <i className="fas fa-times mr-2"></i>
              Annuler
            </button>
            <button 
              type="submit"
              onClick={handleSubmit}
              disabled={loading}
              className={`px-6 py-3 rounded-lg font-medium transition-all ${
                loading
                  ? 'bg-gray-400 cursor-not-allowed text-white'
                  : 'bg-primary-600 hover:bg-primary-700 text-white shadow-lg hover:shadow-xl'
              }`}
            >
              {loading ? (
                <div className="flex items-center">
                  <div className="animate-spin rounded-full h-5 w-5 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </div>
              ) : (
                <div className="flex items-center">
                  <i className="fas fa-save mr-2"></i>
                  Enregistrer l'article
                </div>
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AddArticleModal;